// Atoms pertaining to stored answers
import { atom } from 'jotai/vanilla';
import {
  IAnswer,
  IAnswerType,
  IComponent,
  IMutationResponse,
  IOption,
  IPreparedAnswer,
  ISegmentExperienceObject,
  ISegmentInfo,
  ISegmentSection,
} from '../interfaces';
import api from './api';
import { submit_mutation } from './helpers';
import component from './component';
import frame from './frame';
import error from './error';
import actions, { ActionTypes } from './actions';
import application from './application';
import { userEventHandler } from '../events/section';
import atoms from '.';
import { atomWithStorage } from 'jotai/vanilla/utils';
import { formatAnswer } from '../utils/answer-formatter';
import LOCAL_STORAGE_DATA from '../constants/local-storage-data';

// Stores answer
const data = atomWithStorage<IAnswer[]>(LOCAL_STORAGE_DATA.ANSWERS, []);

const get_data = atom<IComponent[]>((get) => {
  const component_list = get<IComponent[]>(component.list);
  const answer_list = get<IAnswer[]>(data);
  return component_list.map((c: { id: string }) => {
    const answer_index = answer_list.findIndex((a) => a.componentID === c.id);
    if (answer_index === -1) return c;
    const { answer, options, createApplicant, education, availability } = answer_list[answer_index];
    return { ...c, answer: answer ?? options ?? createApplicant ?? education ?? availability };
  });
});
const segment_saved_data = atom<ISegmentSection | null>(null);

// Gets answers only from currently displayed components
const current_answers = atom((get) => {
  const component_ids = get<IComponent[]>(component.list).map((c) => c.id);
  const answer_list = get<IAnswer[]>(data);
  return answer_list.filter((a) => component_ids.includes(a.componentID));
});
const prepare_answers = atom((get) => {
  return get<IAnswer[]>(current_answers).reduce<IPreparedAnswer[]>((acc, a) => {
    const componentData = get<IComponent[]>(component.list).find((c) => c.id === a.componentID);
    const { show } = componentData ?? { show: false };
    if (!show) return acc;

    // Special handling for preferred_subject - ensure it's sent as string
    if (componentData?.action === ActionTypes.preferred_subject && a.answer) {
      return [
        ...acc,
        {
          componentID: a.componentID,
          answer: a.answer.toString(),
        },
      ];
    }

    return [
      ...acc,
      {
        ...(Array.isArray(a.answer) ? { ...a, answer: undefined } : a),
        options: a.options ? a.options?.map((option) => parseInt(option.id)) : undefined,
      },
    ];
  }, []);
});

const prepare_segment_object = (
  accum: ISegmentInfo,
  answer: IAnswer,
  action: string | undefined,
  option: IOption[] | undefined,
  sectionSelected: string | undefined,
) => {
  if (!action && !option && sectionSelected === 'Education') {
    return {
      ...accum,
      ...{
        education: {
          value: answer.education || [],
          componentID: answer.componentID,
        },
      },
    };
  }

  return {
    ...accum,
    ...{
      [action as string]: {
        value:
          action === ActionTypes.country
            ? option?.find((o: { id: string }) => o.id === answer.answer)?.label ?? ''
            : answer.answer,
        componentID: answer.componentID,
      },
    },
  };
};

const prepare_segment = atom((get) => {
  const sectionSelected = get(frame.data).sections.find(
    (section) => section.id === get(frame.data).section,
  )?.name;
  const saved_data = get(segment_saved_data)?.answer;
  return {
    currentSection: sectionSelected || '',
    answer:
      sectionSelected !== 'Experience'
        ? get<IAnswer[]>(current_answers).reduce<ISegmentInfo>((acc, a) => {
            const { show, options, action } = get<IComponent[]>(component.list).find(
              (c) => c.id === a.componentID,
            ) ?? {
              show: false,
            };
            if (!show) return acc;
            return prepare_segment_object(acc, a, action, options, sectionSelected);
          }, saved_data || {})
        : saved_data,
  } as ISegmentSection;
});

const prepare_segment_experience_data = (
  responseComponent: IComponent[],
  states: { options: IOption[] },
) => {
  const experience = responseComponent.reduce(
    (acc, component, index) => {
      if (index === 0 || !component.answer) return acc;
      if (component.action === 'states' && component.answer) {
        const state = states.options.find(
          (state: { id: IAnswerType | undefined }) => state.id === component.answer,
        )?.label;
        const value = {
          label: responseComponent ? component.label : '',
          value: state,
          componentID: component.id,
        } as ISegmentExperienceObject;
        acc.push(value);
        return acc;
      }
      const parseAnswer = component.answer
        ? component.variant !== 'date'
          ? JSON.parse(component.answer as string)
          : component.answer
        : component.options;
      const parseValue = Array.isArray(parseAnswer)
        ? parseAnswer.map(
            (answerId) => component.options?.find((option) => +option.id === +answerId)?.label,
          )
        : component.options?.find((option) => option.id.toString() === component.answer)?.label;
      const value = {
        label: responseComponent ? component.label : '',
        value: component.options ? parseValue : component.answer,
        componentID: component.id,
      } as ISegmentExperienceObject;
      acc.push(value);
      return acc;
    },
    [] as unknown as ISegmentExperienceObject[],
  );

  return { experience: experience };
};

export const get_segment_data = (
  get,
  set,
  response: IMutationResponse,
  states,
): ISegmentSection | null => {
  const featureFlag = get(atoms.segmentFeatureFlag);
  if (!featureFlag) return null;

  const segment_list = get(prepare_segment);
  const segment_data = {
    ...segment_list,
    answer: { ...segment_list.answer, application_id: response.data?.submitApplicationAnswers?.id },
  };
  const segment_experience_data =
    segment_data.currentSection !== 'Experience'
      ? {}
      : prepare_segment_experience_data(
          response.data.submitApplicationAnswers.application?.sections[2].frames[0]
            .components as IComponent[],
          states,
        );
  const final_segment_data = { ...segment_data, ...segment_experience_data };
  set(segment_saved_data, final_segment_data);
  return final_segment_data;
};
// Derived atom for adding an answer to the array.
// If the answer already exists, it's replaced by the new one.
// Otherwise it's added.
const add = atom(null, (get, set, answer: IAnswer) => {
  const unparsed_answer =
    answer?.answer ??
    answer?.options ??
    answer?.createApplicant ??
    answer?.education ??
    answer?.availability;
  const action =
    get<IComponent[]>(component.list).find((c) => c.id === answer.componentID)?.action ?? '';
  const parsed_answer: IAnswerType = set(actions[action].parse, unparsed_answer);
  const new_answer = parsed_answer ? parsed_answer : unparsed_answer;
  set(component.set_props, answer.componentID, {
    answer: new_answer,
  });
  const formatted_answer = formatAnswer(new_answer);
  set(data, (prev) => [
    ...prev.filter((a) => a.componentID !== answer.componentID),
    { ...answer, ...formatted_answer },
  ]);

  set(error.message, ''); // Clear error message
});
// Submits current answers to the backend
const submit = atom(null, async (get, set) => {
  const utm_params = get(application.utm_params);
  const sent_utm_params = get(application.sent_utm_params);
  const app = get(frame.data);
  const answer_list = get(prepare_answers);
  const states = get(actions[ActionTypes.states].data);
  const is_application_id_empty = app.application_id === '';
  const application_id = is_application_id_empty ? undefined : app.application_id;

  const response = (await get(api.client).rawRequest(submit_mutation(), {
    application_id,
    answers: answer_list,
    utm_params: sent_utm_params ? undefined : utm_params,
  })) as IMutationResponse;

  const final_segment_data = get_segment_data(get, set, response, states);
  userEventHandler(final_segment_data);

  if (!sent_utm_params) {
    set(application.sent_utm_params, true);
  }
  const { data, errors } = response;
  if (errors) {
    let new_errors = {};
    try {
      new_errors = errors.reduce((acc, error) => {
        const component_id = error.extensions?.component;
        if (component_id) {
          const component_field = error.extensions.field;
          if (component_field) {
            const old_errors = acc[component_id];
            acc[component_id] = { ...old_errors, [component_field]: error.message };
            return acc;
          }
          acc[component_id] = error.message;
        } else throw new Error(error.message);
        return acc;
      }, {});
    } catch (error) {
      if (typeof error === 'string') {
        // eslint-disable-next-line no-console
        console.error(error);
      }
      // eslint-disable-next-line no-console
      else console.error(errors);
    }
    Object.keys(new_errors).forEach((id) => {
      set(component.set_props, id, { error: new_errors[id], has_error: true });
    });
  }
  if (
    data?.submitApplicationAnswers?.application &&
    data.submitApplicationAnswers.application.sections &&
    data.submitApplicationAnswers.id &&
    data.submitApplicationAnswers.activeSection
  ) {
    const active_section = data.submitApplicationAnswers.activeSection;
    if (active_section) {
      set(frame.update, {
        active_section: {
          application_id: data.submitApplicationAnswers.id,
          button: active_section.button,
          section: active_section.id,
          sections: data.submitApplicationAnswers.application.sections,
          frames: active_section.frames,
        },
        complete: data.submitApplicationAnswers.complete,
      });
    }

    window.scrollTo({ top: 0 });
  }
  set(api.is_loading, false);
});

export default {
  data,
  get_data,
  current_answers,
  prepare_answers,
  prepare_segment,
  add,
  submit,
};
